import { MarketNetworkSchema } from "../cefi/MarketNetwork";
import z from "zod";

export const TokenSymbolSchema = z.string();
export type TokenSymbol = z.infer<typeof TokenSymbolSchema>;

export const PairSymbolSchema = z.string();
export type PairSymbol = z.infer<typeof PairSymbolSchema>;

/**
 * Parsed components of a pair symbol.
 * Supports both spot (BTC/USDT) and perps (BTC/USDT:USDC) formats.
 */
export interface ParsedPairSymbol {
  /** Base token symbol (e.g., "BTC") */
  base: string;
  /** Quote token symbol (e.g., "USDT") */
  quote: string;
  /** Settlement token for perpetual pairs (e.g., "USDC"), undefined for spot */
  settlement?: string;
}

// Token metadata types (moved from api/tokens.ts to avoid circular dependency)
export const TokenSymbolNameSchema = z.object({
  symbol: z.string(),
  name: z.string(),
});

export type TokenSymbolName = z.infer<typeof TokenSymbolNameSchema>;

// Token with network types (moved from untrustedApi/types.ts to avoid circular dependency)
export const TokenWithMarketNetworkSchema = z.object({
  tokenSymbol: TokenSymbolSchema,
  tokenIcon: z.string().nullable().optional(),
  isDepositable: z.boolean(),
  isWithdrawable: z.boolean(),
  networks: z.array(MarketNetworkSchema),
  precision: z.number().nullable().optional(),
  contracts: z.record(z.string(), z.string()).nullable().optional(),
});

export type TokenWithMarketNetwork = z.infer<typeof TokenWithMarketNetworkSchema>;

export const TokenWithMarketNetworkAndTokenNameSchema = TokenWithMarketNetworkSchema.extend({
  name: z.string().nullable().optional(),
});

export type TokenWithMarketNetworkAndTokenName = z.infer<typeof TokenWithMarketNetworkAndTokenNameSchema>;

// Exchange status enum (moved from api/exchange.ts to avoid circular dependency)
export enum ExchangeStatus {
  Ok = "ok",
  Shutdown = "shutdown",
  Error = "error",
  Maintenance = "maintenance",
  Unknown = "unknown",
}
export const EXCHANGE_STATUS = {
  OK: "ok",
  SHUTDOWN: "shutdown",
  ERROR: "error",
  MAINTENANCE: "maintenance",
  UNKNOWN: "unknown",
} as const;
export type EXCHANGE_STATUS = (typeof EXCHANGE_STATUS)[keyof typeof EXCHANGE_STATUS];

// OHLCV types (moved from trade/OHLCV.ts to avoid circular dependency)
export const OHLCVSchema = z.object({
  time: z.number(),
  high: z.number(),
  low: z.number(),
  open: z.number(),
  close: z.number(),
  volume: z.number(),
});
export type OHLCV = z.infer<typeof OHLCVSchema>;

export enum Timeframe {
  "1m" = "1m",
  "5m" = "5m",
  "15m" = "15m",
  "30m" = "30m",
  "1h" = "1h",
  "4h" = "4h",
  "6h" = "6h",
  "12h" = "12h",
  "1d" = "1d",
  "1w" = "1w",
  "1M" = "1M",
}

// Order side types
export const ORDER_SIDE = {
  BUY: "buy",
  SELL: "sell",
} as const;
export type ORDER_SIDE = (typeof ORDER_SIDE)[keyof typeof ORDER_SIDE];
export type OrderSide = "buy" | "sell"; // for backward compatibility

// Position and margin types
export const POSITION_SIDE = {
  LONG: "long",
  SHORT: "short",
} as const;
export type PositionSide = (typeof POSITION_SIDE)[keyof typeof POSITION_SIDE];
export type POSITION_SIDE = (typeof POSITION_SIDE)[keyof typeof POSITION_SIDE];

export enum MarginMode {
  CROSS = "cross",
  ISOLATED = "isolated",
  UNKNOWN = "unknown",
}
export const MARGIN_MODE = {
  CROSS: "cross",
  ISOLATED: "isolated",
  UNKNOWN: "unknown",
} as const;
export type MARGIN_MODE = (typeof MARGIN_MODE)[keyof typeof MARGIN_MODE];

/**
 * Declares what an exchange permits for the venue-side margin mode.
 *
 * - `both`         — venue supports cross AND isolated; setLeverage honors caller's choice.
 * - `cross-only`   — venue forces cross (e.g. Paradex, Extended, ZeroOne).
 * - `isolated-only`— venue forces isolated (e.g. Decibel).
 * - `none`         — venue has no margin-mode concept (e.g. Hibachi: single IMR scalar).
 */
export const MARGIN_MODE_POLICY = {
  BOTH: "both",
  CROSS_ONLY: "cross-only",
  ISOLATED_ONLY: "isolated-only",
  NONE: "none",
} as const;
export type MarginModePolicy = (typeof MARGIN_MODE_POLICY)[keyof typeof MARGIN_MODE_POLICY];

// Order type enum and const
export enum OrderType {
  LIMIT = "limit",
  MARKET = "market",
  UNKNOWN = "unknown",
}
export const ORDER_TYPE = {
  LIMIT: "limit",
  MARKET: "market",
  UNKNOWN: "unknown",
} as const;
export type ORDER_TYPE = (typeof ORDER_TYPE)[keyof typeof ORDER_TYPE];

// Order status enum and const
export enum OrderStatus {
  OPEN = "open",
  CLOSED = "closed",
  CANCELED = "canceled",
  REJECTED = "rejected",
  UNKNOWN = "unknown",
}
export const ORDER_STATUS = {
  OPEN: "open",
  CLOSED: "closed",
  CANCELED: "canceled",
  REJECTED: "rejected",
  UNKNOWN: "unknown",
} as const;
export type ORDER_STATUS = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];

// Campaign eligibility reasons
export const NON_ELIGIBILITY_REASONS = {
  STP: "STP",
  MISSING_REQUIRED_SOCIAL_ACCOUNTS: "MISSING_REQUIRED_SOCIAL_ACCOUNTS",
  NO_CAMPAIGN_FOUND: "NO_CAMPAIGN_FOUND",
  INVALID_CAMPAIGN_SYMBOL: "INVALID_CAMPAIGN_SYMBOL",
  RESTRICTED_PARTICIPANTS: "RESTRICTED_PARTICIPANTS",
  TRADE_PAGE_INELIGIBLE: "TRADE_PAGE_INELIGIBLE", // Trade page orders that don't pass Hall of Fame eligibility (stablecoin pairs, zero-fee pairs, disabled exchanges, etc.)
  UNDEFINED_CAMPAIGN_ID: "UNDEFINED_CAMPAIGN_ID",
  INVALID_MARKET_TYPE: "INVALID_MARKET_TYPE", // Trade was on wrong market type (spot vs perps)
  STREAK_ONLY_MODE: "STREAK_ONLY_MODE", // Manual trades not eligible when campaign is in streak_only mode
  WALLET_TOO_NEW: "WALLET_TOO_NEW", // Wallet's oldest transaction is less than 2 weeks old (DEX campaigns only)
  INVALID_PAIR_SYMBOL: "INVALID_PAIR_SYMBOL", // Trade pair not in campaign's whitelisted pairSymbols
  EXTERNAL_ORDER_DETECTED: "EXTERNAL_ORDER_DETECTED", // User placed an order directly on the exchange, not through the hub (PnL perps campaigns)
  PERPS_PNL_OPT_IN_REQUIRED: "PERPS_PNL_OPT_IN_REQUIRED", // User has not opted in for the PnL perps campaign epoch
} as const;
export type NON_ELIGIBILITY_REASONS = (typeof NON_ELIGIBILITY_REASONS)[keyof typeof NON_ELIGIBILITY_REASONS];
